import type { PaginationState, SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { parseSort } from '@/features/report/columns'
import type { ReportFilters } from '@/features/report/use-report-filters'
import { useTablePreferences } from '@/hooks/use-table-preferences'
import type { AgingMode } from '@/lib/aging'
import {
  shortName,
  type AgingWidget,
  type ChartWidget,
  type DashboardWidget,
  type KpiWidget,
  type ListWidget,
  type TableWidget,
} from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { listPageQuery, widgetFilters } from '@/services/cube/dashboard'
import { columnValuesQuery, fetchAllRows } from '@/services/cube/report'

import { AgingWidgetView } from './AgingWidget'
import { ChartWidgetView } from './ChartWidget'
import { KpiWidgetView } from './KpiWidget'
import { ListWidgetView } from './ListWidget'
import { InTabs } from './shared'
import { TableWidgetView } from './TableWidget'
import {
  useAgingData,
  useChartData,
  useKpiData,
  useListData,
  useTableData,
} from './use-widget-data'

/*
 * Canlı parçalar: veri hook'u + saf çizim. Pano `live` (varsayılan) ile WS'e abone
 * olur; şablon önizlemesi `live={false}` ile yalnız HTTP yükler.
 */

interface LiveProps<W> {
  widget: W
  filters: ReportFilters
  live?: boolean
}

function LiveList({ widget, filters, live }: LiveProps<ListWidget>) {
  const { state, get, set } = filters
  const param = (name: string) => (widget.urlKey ? `${widget.urlKey}_${name}` : name)
  const page = Number(get(param('page')) ?? '1')
  const pageSize = Number(get(param('page_size')) ?? '50')
  const sortRaw = get(param('sort'))

  const tableId = widget.view.toLowerCase()
  const preferences = useTablePreferences(tableId, {})
  const sorting: SortingState = useMemo(() => parseSort(sortRaw, widget), [sortRaw, widget])
  const pagination: PaginationState = useMemo(
    () => ({ pageIndex: Math.max(page - 1, 0), pageSize }),
    [page, pageSize],
  )
  const sortItems = sorting.map((item) => ({ member: item.id, desc: item.desc }))
  const { data, status } = useListData(
    widget,
    state,
    sortItems,
    pagination.pageIndex + 1,
    pageSize,
    { live },
  )

  return (
    <ListWidgetView
      widget={widget}
      data={data}
      status={status}
      controls={{
        pagination,
        onPaginationChange: (updater) => {
          const next = typeof updater === 'function' ? updater(pagination) : updater
          set({
            [param('page')]: String(next.pageIndex + 1),
            [param('page_size')]: String(next.pageSize),
          })
        },
        sorting,
        onSortingChange: (updater) => {
          const next = typeof updater === 'function' ? updater(sorting) : updater
          const serialised = next
            .filter((item) => widget.sortable.has(item.id))
            .map((item) => `${item.desc ? '-' : ''}${item.id}`)
            .join(',')
          set({ [param('sort')]: serialised || undefined, [param('page')]: undefined })
        },
        layout: preferences.layout,
        onLayoutChange: preferences.save,
        onLayoutReset: preferences.reset,
        columnFilters: state.columns,
        onColumnFiltersChange: filters.setColumns,
        filterValues: (member, search) =>
          columnValuesQuery(
            widget.view,
            member,
            search,
            (scoped) => widgetFilters(widget, scoped),
            state,
          ),
        fetchExportData: () => fetchAllRows(listPageQuery(widget, state, sortItems, 1, pageSize)),
      }}
    />
  )
}

export function LiveWidgetView({ widget, filters, live }: LiveProps<DashboardWidget>) {
  switch (widget.type) {
    case 'kpi':
      return <LiveKpi widget={widget} filters={filters} live={live} />
    case 'table':
      return <LiveTable widget={widget} filters={filters} live={live} />
    case 'list':
      return <LiveList widget={widget} filters={filters} live={live} />
    case 'chart':
      return <LiveChart widget={widget} filters={filters} live={live} />
    case 'aging':
      return <LiveAging widget={widget} filters={filters} live={live} />
  }
}

function LiveAging({ widget, filters, live }: LiveProps<AgingWidget>) {
  const { state, get, set } = filters
  const preferences = useTablePreferences(`${widget.view.toLowerCase()}-aging`, {})
  const { data, status } = useAgingData(widget, state, { live })
  return (
    <AgingWidgetView
      widget={widget}
      data={data}
      status={status}
      controls={{
        mode: get('aging') === 'month' ? 'month' : 'day',
        onModeChange: (mode: AgingMode) => set({ aging: mode === 'day' ? undefined : mode }),
        layout: preferences.layout,
        onLayoutChange: preferences.save,
        onLayoutReset: preferences.reset,
        columnFilters: state.columns,
        onColumnFiltersChange: filters.setColumns,
        filterValues: (member, search) =>
          columnValuesQuery(
            widget.view,
            member,
            search,
            (scoped) => widgetFilters(widget, scoped),
            state,
          ),
      }}
    />
  )
}

function LiveKpi({ widget, filters, live }: LiveProps<KpiWidget>) {
  const { data, status, fxFailed } = useKpiData(widget, filters.state, { live })
  const [open, setOpen] = useState(false)
  const drill = widget.drill
  return (
    <>
      <KpiWidgetView
        widget={widget}
        data={data}
        status={status}
        fxFailed={fxFailed}
        onOpen={drill ? () => setOpen(true) : undefined}
      />
      {drill && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-[min(calc(100vw-2rem),1200px)]">
            <DialogHeader>
              <DialogTitle>{drill.title}</DialogTitle>
              <DialogDescription>{messages.report.drillDescription}</DialogDescription>
            </DialogHeader>
            {open && <DrillList widget={drill} filters={filters} live={live} />}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

/**
 * KPI kaynak satırları: sayfa filtreleri adres çubuğundan, sayfa/sıra/sütun filtresi
 * pencereye özel (kapanınca unutulur, sayfanın listesini etkilemez).
 */
function DrillList({ widget, filters, live }: LiveProps<ListWidget>) {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 })
  const [sorting, setSorting] = useState<SortingState>(() =>
    widget.defaultSort.map((item) => ({ id: item.member, desc: item.desc })),
  )
  const [columns, setColumns] = useState(filters.state.columns)
  const state = useMemo(() => ({ ...filters.state, columns }), [filters.state, columns])
  const preferences = useTablePreferences(`${widget.view.toLowerCase()}-drill`, {})
  const sortItems = sorting.map((item) => ({ member: item.id, desc: item.desc }))
  const { data, status } = useListData(
    widget,
    state,
    sortItems,
    pagination.pageIndex + 1,
    pagination.pageSize,
    { live },
  )
  return (
    <InTabs.Provider value>
      <ListWidgetView
        widget={widget}
        data={data}
        status={status}
        controls={{
          pagination,
          onPaginationChange: (updater) =>
            setPagination(typeof updater === 'function' ? updater(pagination) : updater),
          sorting,
          onSortingChange: (updater) => {
            const next = typeof updater === 'function' ? updater(sorting) : updater
            setSorting(next.filter((item) => widget.sortable.has(item.id)))
            setPagination((current) => ({ ...current, pageIndex: 0 }))
          },
          layout: preferences.layout,
          onLayoutChange: preferences.save,
          onLayoutReset: preferences.reset,
          columnFilters: columns,
          onColumnFiltersChange: (next) => {
            setColumns(next)
            setPagination((current) => ({ ...current, pageIndex: 0 }))
          },
          filterValues: (member, search) =>
            columnValuesQuery(
              widget.view,
              member,
              search,
              (scoped) => widgetFilters(widget, scoped),
              state,
            ),
          fetchExportData: () =>
            fetchAllRows(listPageQuery(widget, state, sortItems, 1, pagination.pageSize)),
        }}
      />
    </InTabs.Provider>
  )
}

function LiveTable({ widget, filters, live }: LiveProps<TableWidget>) {
  const { data, status } = useTableData(widget, filters.state, { live })
  return (
    <TableWidgetView
      widget={widget}
      data={data}
      status={status}
      selection={{ selections: filters.state.selections, onSelect: filters.select }}
    />
  )
}

function LiveChart({ widget: base, filters, live }: LiveProps<ChartWidget>) {
  // Seçilen kırılım adres çubuğunda (kısa ad); varsayılan seçenek parametre yazmaz.
  const param = `kirilim_${base.key}`
  const options = base.breakdownOptions
  const chosen = options?.find((name) => shortName(name) === filters.get(param))
  const widget = useMemo(
    () => (chosen && chosen !== base.breakdown ? { ...base, breakdown: chosen } : base),
    [base, chosen],
  )
  const { data, status } = useChartData(widget, filters.state, { live })
  return (
    <ChartWidgetView
      widget={widget}
      data={data}
      status={status}
      selection={{ selections: filters.state.selections, onSelect: filters.select }}
      onBreakdownChange={
        options
          ? (member) =>
              filters.set({ [param]: member === options[0] ? undefined : shortName(member) })
          : undefined
      }
    />
  )
}
